import { useCallback, useEffect, useRef, useState } from 'react';
import { readCompanyLogo } from '../../data/queries/use-company-branding';
import type { CompanyBrandingSummary } from '../../data/queries/use-company-branding';

/**
 * The company logo's bytes, fetched only when a screen actually draws them.
 * M10 / S10-03B.
 *
 * **THIS IS THE FIRST LEGITIMATE CALLER OF THE SEPARATE LOGO READ.** S10-03A
 * deliberately kept `logo_base64` out of the ordinary branding query, because it
 * is the only large column in the schema and this repository reads with
 * `select('*')` almost everywhere. The preview is the one screen with a real
 * reason to ask — it puts the logo on the page.
 *
 * **NOTHING IS FETCHED WHEN THERE IS NOTHING TO DRAW**: branding turned off, or
 * branding with no logo, asks for nothing at all.
 */
export function useCompanyLogoBytes(branding: CompanyBrandingSummary | null): string | null {
  const [base64, setBase64] = useState<string | null>(null);
  const hasLogo = Boolean(branding?.logo_mime);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const apply = useCallback((next: string | null) => {
    if (alive.current) setBase64(next);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!hasLogo) {
      apply(null);
      return;
    }
    apply(await readCompanyLogo());
  }, [hasLogo, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  return hasLogo ? base64 : null;
}
